import React, { FunctionComponent } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
// Import each provider from its own module rather than the '@/contexts' barrel.
// The barrel re-exports GlobalProviders from './global', so importing the barrel
// here creates a circular dependency (index.ts <-> global.tsx). Under Rolldown's
// code-splitting that cycle let context modules (e.g. websocket.tsx) get duplicated
// into lazy widget chunks, so the Provider and a lazy consumer's useContext bound
// to different context objects -> "useWebSocket must be used within a WebSocketProvider".
import { AuthProvider } from './auth';
import { ConfirmationProvider } from './confirmation';
import { WebSocketProvider } from './websocket';
import { GlobalBannerProvider } from './GlobalBannerContext';
import { PinProtectionProvider } from './pinProtection';
import { ThemeProvider } from '@/contexts/theme';
import { ToastProvider } from '@/components/Toast/ToastContext';
import { auth0ProviderOptions } from '@/auth/providerOptions';

// Global query defaults. Previously the client had none, so every useQuery inherited
// staleTime:0 + refetchOnWindowFocus:true — i.e. ~90% of the app's 400+ queries refetched
// on every route mount AND every time the vendor tabbed back to the window, re-waiting on
// the API on each navigation. A modest staleTime keeps data fresh across quick navigations
// without refetching, and disabling focus-refetch stops the "everything reloads when I come
// back" churn. Mutations still call invalidateQueries explicitly, so writes refresh as before.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false
    }
  }
});

interface GlobalProvidersPropsI {
  children?: React.ReactNode;
}

export const GlobalProviders: FunctionComponent<GlobalProvidersPropsI> = ({ children }) => {
  return (
    <ThemeProvider>
      <ToastProvider position="bottom-left" maxToasts={5}>
        <QueryClientProvider client={queryClient}>
          <Auth0Provider {...auth0ProviderOptions}>
            <AuthProvider>
              <PinProtectionProvider>
                <WebSocketProvider>
                  <GlobalBannerProvider>
                    <ConfirmationProvider>{children}</ConfirmationProvider>
                  </GlobalBannerProvider>
                </WebSocketProvider>
              </PinProtectionProvider>
            </AuthProvider>
          </Auth0Provider>
        </QueryClientProvider>
      </ToastProvider>
    </ThemeProvider>
  );
};
